
import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { COST_CATEGORIES, formatCurrency, formatDate, formatMonthForDisplay } from '../constants';
import { Project, Company, ExpenseEntry, Attachment } from '../types';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ArrowLeftIcon } from '../components/icons/ArrowLeftIcon';
import { DetailItem } from '../components/DetailItem';
import { InfoCardItem } from '../components/InfoCardItem';
import { useExpenses } from '../hooks/useExpenses';
import { useUsers } from '../hooks/useUsers';
import { useProjects } from '../hooks/useProjects';
import { useCompanies } from '../hooks/useCompanies';
import { AttachmentViewerModal } from '../components/AttachmentViewerModal';

// Project Header Icons
import { UserIcon } from '../components/icons/UserIcon';
import { IdentificationIcon } from '../components/icons/IdentificationIcon';
import { ClipboardDocumentListIcon } from '../components/icons/ClipboardDocumentListIcon';
import { MapPinIcon } from '../components/icons/MapPinIcon';
import { PhoneIcon } from '../components/icons/PhoneIcon';
// Expense Detail Icons
import { BriefcaseIcon } from '../components/icons/BriefcaseIcon';
import { DocumentTextIcon } from '../components/icons/DocumentTextIcon';
import { CalendarIcon } from '../components/icons/CalendarIcon';
import { ListBulletIcon } from '../components/icons/ListBulletIcon';
import { BanknotesIcon } from '../components/icons/BanknotesIcon';
import { InformationCircleIcon } from '../components/icons/InformationCircleIcon';
import { EyeIcon } from '../components/icons/EyeIcon';
import { DownloadIcon } from '../components/icons/DownloadIcon';


export const ExpenseDetailPage: React.FC = () => {
    const { obraId, categoryId, subItemId, expenseId } = useParams<{ obraId: string, categoryId: string, subItemId: string, expenseId: string }>();
    const navigate = useNavigate();
    const { allExpenses } = useExpenses();
    const { users } = useUsers();
    const { projects } = useProjects();
    const { companies } = useCompanies();
    const [viewingAttachment, setViewingAttachment] = useState<Attachment | null>(null);

    const project = useMemo(() => projects.find(p => p.id === obraId), [projects, obraId]);
    const expense = useMemo(() => allExpenses.find(e => e.id === expenseId), [allExpenses, expenseId]);
    const company = useMemo(() => project ? companies.find(c => c.id === project.companyId) : undefined, [project, companies]);

    const handleBackNavigation = () => {
        if (!expense || !categoryId) return;
        
        const categoryDetails = COST_CATEGORIES.find(cat => cat.id === categoryId);
        
        const isDirectEntry = categoryDetails?.subItems.length === 1 && categoryDetails.subItems[0].id.includes('geral_lancamento');
        
        if(isDirectEntry) {
            navigate(`/obras/${obraId}/custos/${categoryId}`);
        } else {
            navigate(`/obras/${obraId}/custos/${categoryId}/${subItemId}`);
        }
    };


    const handleViewAttachment = (att: Attachment) => {
        setViewingAttachment(att);
    };

    if (!project || !expense || !company) {
        return (
            <div className="text-center py-10">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">Dados do lançamento ou da obra não encontrados.</h1>
                <Button onClick={() => navigate('/obras')} variant="secondary" className="mt-4">Voltar</Button>
            </div>
        );
    }
    
    const requester = useMemo(() => users.find(e => e.id === expense.requesterId), [users, expense.requesterId]);
    
    // Config to render expense details dynamically
    const fieldConfig: { [key: string]: { label: string; icon: React.FC<{ className?: string }>; value: any; render?: (val: any) => React.ReactNode } } = {
        supplier: { label: 'Fornecedor', icon: BriefcaseIcon, value: expense.supplier },
        invoice: { label: 'Nº Nota Fiscal', icon: DocumentTextIcon, value: expense.invoice },
        date: { label: 'Data do Lançamento', icon: CalendarIcon, value: expense.date, render: formatDate },
        month: { label: 'Mês de Competência', icon: CalendarIcon, value: expense.month, render: formatMonthForDisplay },
        quantity: { label: 'Quantidade', icon: ListBulletIcon, value: expense.quantity },
        unitPrice: { label: 'Valor Unitário', icon: BanknotesIcon, value: expense.unitPrice, render: formatCurrency },
        amount: { label: 'Valor Total', icon: BanknotesIcon, value: expense.amount, render: formatCurrency },
        requesterId: { label: 'Requisitante', icon: UserIcon, value: requester?.name },
        description: { label: 'Descrição', icon: ClipboardDocumentListIcon, value: expense.description },
        observation: { label: 'Observação', icon: InformationCircleIcon, value: expense.observation },
        contract: { label: 'Pedido/Contrato', icon: DocumentTextIcon, value: expense.contract },
        contact: { label: 'Contato', icon: PhoneIcon, value: expense.contact },
        licensePlate: { label: 'Placa Veículo', icon: IdentificationIcon, value: expense.licensePlate },
        unit: { label: 'Unidade', icon: ListBulletIcon, value: expense.unit },
    };

    return (
        <div className="text-gray-900 dark:text-dark-text-primary space-y-6">
            {/* Header */}
            <div className="flex items-center">
                <Button variant="ghost" size="sm" onClick={handleBackNavigation} className="p-2 mr-2 hover:bg-gray-100 dark:hover:bg-dark-tertiary" aria-label="Voltar">
                    <ArrowLeftIcon className="text-2xl" />
                </Button>
                <h1 className="text-2xl font-semibold">{expense.subCategoryItemName}</h1>
            </div>

            {/* Project Header */}
            <Card title="Informações da Obra">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    <InfoCardItem icon={<UserIcon className="text-xl text-brand-teal" />} label="Encarregado" value={project.personInCharge || 'Não informado'} />
                    <InfoCardItem icon={<MapPinIcon className="text-xl text-brand-teal" />} label="Endereço" value={company ? `${company.address.street}, ${company.address.city}/${company.address.state}` : 'Não informado'} />
                    <InfoCardItem icon={<IdentificationIcon className="text-xl text-brand-teal" />} label="CNPJ" value={company?.cnpj || 'Não informado'} />
                    <InfoCardItem icon={<PhoneIcon className="text-xl text-brand-teal" />} label="Telefone" value={company?.phone || 'Não informado'} />
                    <InfoCardItem icon={<ClipboardDocumentListIcon className="text-xl text-brand-teal" />} label="Descrição do Serviço" value={project.serviceDescription || 'Não informado'} isBlock />
                </div>
            </Card>

            {/* Expense Details */}
            <Card title="Detalhes do Lançamento">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                    {Object.entries(fieldConfig).map(([key, config]) => {
                        if (config.value === undefined || config.value === null || config.value === '') return null;
                        return (
                            <DetailItem 
                                key={key}
                                icon={<config.icon className="text-xl text-brand-teal" />}
                                label={config.label}
                                value={config.render ? config.render(config.value) : config.value}
                            />
                        );
                    })}
                </div>
            </Card>

            {/* Attachments */}
            {expense.attachments && expense.attachments.length > 0 && (
                <Card title="Anexos">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {expense.attachments.map((att: Attachment) => (
                            <div key={att.id} className="relative group bg-gray-100 dark:bg-dark-secondary rounded-md border border-gray-200 dark:border-dark-tertiary aspect-square flex items-center justify-center p-2 text-center transition-colors">
                                {att.type.startsWith('image/') ? (
                                    <img src={att.url} alt={att.name} className="max-w-full max-h-full object-contain" />
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <DocumentTextIcon className="text-5xl text-gray-500 dark:text-dark-text-secondary" />
                                        <p className="text-xs text-gray-500 dark:text-dark-text-secondary truncate w-full px-1 mt-1" title={att.name}>{att.name}</p>
                                    </div>
                                )}
                                 <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center space-x-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <button onClick={() => handleViewAttachment(att)} title="Visualizar" className="p-2 text-white rounded-full hover:bg-white/20 transition-colors">
                                        <EyeIcon className="text-3xl" />
                                    </button>
                                    <a href={att.url} download={att.name} title="Baixar" className="p-2 text-white rounded-full hover:bg-white/20 transition-colors">
                                        <DownloadIcon className="text-3xl" />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            <AttachmentViewerModal
                isOpen={!!viewingAttachment}
                onClose={() => setViewingAttachment(null)}
                attachment={viewingAttachment}
            />
        </div>
    );
};
